import { Link } from "react-router-dom";
import { ArrowUpRight, HardHat } from "lucide-react";
import { IMAGES } from "../data/site";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";

const CATEGORIES = [
  "All Work",
  "Horizontal Directional Drilling",
  "Underground Utilities",
  "Auger Boring",
  "OSP & Telecom",
  "Trenching",
];

const TRACK_RECORD = [
  { val: "$12M", label: "Largest project completed" },
  { val: "$2K – $3M", label: "Typical project range" },
  { val: "46 Yrs", label: "Continuous field operations" },
];

export default function Projects() {
  return (
    <>
      <PageHero
        testId="projects-hero"
        eyebrow="Work Showcase // Case Dossiers"
        lines={["The work speaks", "underground."]}
        sub="Our best work is buried beneath roads, rivers, and rail corridors across California, Nevada & Arizona. Project dossiers and engineering submittals are being prepared for this showcase."
        image={IMAGES.trenching}
      />

      <section data-testid="projects-structure" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2" data-testid="projects-categories">
              {CATEGORIES.map((c, i) => (
                <span
                  key={c}
                  className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] ${
                    i === 0
                      ? "border-brand bg-brand/15 text-blueprint"
                      : "border-white/15 text-slate-400"
                  }`}
                >
                  {c}
                </span>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((n) => (
              <Reveal key={n} delay={n * 0.08}>
                <div
                  data-testid={`project-slot-${n + 1}`}
                  className="blueprint-grid relative flex aspect-[4/3] flex-col items-center justify-center bg-granite p-8 text-center"
                >
                  <HardHat className="h-8 w-8 text-slate-600" />
                  <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                    Dossier {String(n + 1).padStart(2, "0")}
                  </p>
                  <p className="mt-2 font-display text-2xl font-bold uppercase text-slate-300">
                    Coming Soon
                  </p>
                  <p className="mt-2 max-w-xs text-xs leading-relaxed text-slate-500">
                    Project documentation and field photography being compiled.
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3" data-testid="projects-track-record">
              {TRACK_RECORD.map((t) => (
                <div key={t.label} className="bg-obsidian p-6 sm:p-8 text-center">
                  <p className="font-display text-4xl font-black text-brand-light sm:text-5xl">{t.val}</p>
                  <p className="mt-2 text-sm text-slate-400">{t.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="projects-cta" className="border-t border-white/10 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">References On Request</Eyebrow>
            <h2 className="mx-auto mt-6 max-w-3xl font-display text-4xl sm:text-6xl font-black uppercase leading-[0.95] tracking-tight text-white">
              Need project dossiers for your bid review?
            </h2>
            <Link
              to="/request-bid"
              data-testid="projects-cta-contact"
              className="mt-10 inline-flex items-center gap-2 bg-brand px-8 py-4 text-sm font-semibold text-white transition-colors hover:bg-brand-light"
            >
              Request Submittals <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
